import {
  ChevronRight,
  ExternalLink,
  Eye,
  FileText,
  Folder,
  FolderOpen,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useAuth } from '../auth/useAuth';
import { DriveFileIcon } from '../components/DriveFileIcon';
import {
  buildDriveBrowserIndex,
  driveFileKind,
  formatDriveFileSize,
  isDriveFolder,
  type DriveBrowserItem,
} from '../lib/drive-browser';
import { requireFirebaseFunctions } from '../lib/firebase';

type DriveWorkspace = {
  folder: {
    id: string;
    name: string;
    webViewLink?: string;
  };
  items: DriveBrowserItem[];
  fileCount: number;
  folderCount: number;
  truncated: boolean;
};

type LoadStatus = 'loading' | 'ready' | 'error';

const demoWorkspace: DriveWorkspace = {
  folder: { id: 'demo-root', name: 'Report' },
  fileCount: 3,
  folderCount: 3,
  truncated: false,
  items: [
    { id: '2025', name: '2025', path: '2025', mimeType: 'application/vnd.google-apps.folder' },
    { id: '2026', name: '2026', path: '2026', mimeType: 'application/vnd.google-apps.folder' },
    { id: 'august', name: '08. Agosto 2026', path: '2026/08. Agosto 2026', mimeType: 'application/vnd.google-apps.folder' },
    { id: 'social', name: 'Report Analytics Social OpenFiber_Agosto 2026', path: '2026/08. Agosto 2026/Report Analytics Social OpenFiber_Agosto 2026', mimeType: 'application/vnd.google-apps.spreadsheet' },
    { id: 'slides', name: 'OF_Digital Comm_Report_Agosto+trend_2026', path: '2026/08. Agosto 2026/OF_Digital Comm_Report_Agosto+trend_2026', mimeType: 'application/vnd.google-apps.presentation' },
    { id: 'highlights', name: 'Highlights - Report OF agosto2026.docx', path: '2026/08. Agosto 2026/Highlights - Report OF agosto2026.docx', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
  ],
};

async function fetchDriveWorkspace(demo: boolean, refresh: boolean): Promise<DriveWorkspace> {
  if (demo) return demoWorkspace;

  const getDriveWorkspace = httpsCallable<{ refresh?: boolean }, DriveWorkspace>(
    requireFirebaseFunctions(),
    'getDriveWorkspace',
  );
  const result = await getDriveWorkspace({ refresh });
  return result.data;
}

function formatModifiedTime(value?: string): string {
  if (!value) return 'Non disponibile';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Non disponibile';
  return new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function SourcesPage() {
  const { user } = useAuth();
  const browserRef = useRef<HTMLDivElement>(null);
  const [workspace, setWorkspace] = useState<DriveWorkspace | null>(null);
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [folderTrail, setFolderTrail] = useState<string[]>([]);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void fetchDriveWorkspace(Boolean(user?.demo), false)
      .then((result) => {
        if (!active) return;
        setWorkspace(result);
        setStatus('ready');
      })
      .catch(() => {
        if (active) setStatus('error');
      });
    return () => { active = false; };
  }, [user?.demo]);

  const refreshWorkspace = async () => {
    setStatus('loading');
    try {
      setWorkspace(await fetchDriveWorkspace(Boolean(user?.demo), true));
      setFolderTrail([]);
      setSelectedPath(null);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  };

  const index = useMemo(
    () => buildDriveBrowserIndex(workspace?.items ?? []),
    [workspace?.items],
  );

  const selectedItem = selectedPath ? index.byPath.get(selectedPath) ?? null : null;
  const columns = useMemo(() => {
    if (!workspace) return [];
    return [
      { path: '', name: workspace.folder.name, items: index.childrenByParent.get('') ?? [] },
      ...folderTrail.map((path) => ({
        path,
        name: index.byPath.get(path)?.name ?? path,
        items: index.childrenByParent.get(path) ?? [],
      })),
    ];
  }, [folderTrail, index, workspace]);

  useEffect(() => {
    browserRef.current?.scrollTo({ left: browserRef.current.scrollWidth, behavior: 'smooth' });
  }, [folderTrail, selectedPath]);

  const selectItem = (item: DriveBrowserItem, columnIndex: number) => {
    setSelectedPath(item.path);
    if (isDriveFolder(item)) {
      setFolderTrail((current) => [...current.slice(0, columnIndex), item.path]);
    } else {
      setFolderTrail((current) => current.slice(0, columnIndex));
    }
  };

  const openItem = (item: DriveBrowserItem, columnIndex: number) => {
    if (isDriveFolder(item)) {
      selectItem(item, columnIndex);
      return;
    }
    if (item.webViewLink) window.open(item.webViewLink, '_blank', 'noopener,noreferrer');
  };

  const handleItemKeyDown = (event: KeyboardEvent<HTMLButtonElement>, item: DriveBrowserItem, columnIndex: number) => {
    if (event.key === 'Enter') openItem(item, columnIndex);
  };

  const openSelected = () => {
    if (selectedItem?.webViewLink) window.open(selectedItem.webViewLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <main className="dashboard-page sources-page">
      <header className="page-heading sources-heading">
        <div>
          <p className="app-eyebrow">Salesforce e Drive</p>
          <h1>Drive</h1>
          <p>Esplora i file disponibili nel workspace Drive condiviso.</p>
        </div>
        <span className="drive-readonly-badge"><ShieldCheck size={15} /> Sola lettura</span>
      </header>

      <section className="drive-browser-card" aria-label="File disponibili su Google Drive">
        <header className="drive-browser-toolbar">
          <div className="drive-browser-title">
            <span><FolderOpen size={20} /></span>
            <div>
              <strong>{workspace?.folder.name ?? 'Google Drive'}</strong>
              <small>{status === 'ready' && workspace ? `${workspace.fileCount} file · ${workspace.folderCount} cartelle` : 'Caricamento inventario…'}</small>
            </div>
          </div>
          <div className="drive-browser-actions">
            <span><Eye size={14} /> Visualizzazione</span>
            <button type="button" aria-label="Aggiorna file Drive" disabled={status === 'loading'} onClick={() => void refreshWorkspace()}>
              <RefreshCw className={status === 'loading' ? 'spin' : undefined} size={16} /> Aggiorna
            </button>
          </div>
        </header>

        <div className="drive-pathbar" aria-label="Percorso corrente">
          <span>{workspace?.folder.name ?? 'Report'}</span>
          {folderTrail.map((path) => <span key={path}>{index.byPath.get(path)?.name ?? path}</span>)}
        </div>

        {status === 'loading' && (
          <div className="drive-browser-state"><LoaderCircle className="spin" size={25} /><strong>Leggo il workspace Drive</strong><p>Sto ricostruendo cartelle e file.</p></div>
        )}

        {status === 'error' && (
          <div className="drive-browser-state error"><FileText size={25} /><strong>Drive non disponibile</strong><p>Non è stato possibile leggere la cartella condivisa.</p><button className="button button-secondary" type="button" onClick={() => void refreshWorkspace()}>Riprova</button></div>
        )}

        {status === 'ready' && workspace && (
          <>
            <div className="drive-column-browser" ref={browserRef}>
              {columns.map((column, columnIndex) => (
                <section className="drive-column" key={column.path || 'root'} aria-label={`Contenuto di ${column.name}`}>
                  <header><Folder size={14} /><strong>{column.name}</strong><span>{column.items.length}</span></header>
                  <div className="drive-column-list">
                    {column.items.length === 0 && <p className="drive-column-empty">Cartella vuota</p>}
                    {column.items.map((item) => {
                      const selected = selectedPath === item.path;
                      return (
                        <button
                          className={selected ? 'selected' : undefined}
                          type="button"
                          key={item.id}
                          title={isDriveFolder(item) ? `Apri ${item.name}` : `Doppio clic per visualizzare ${item.name}`}
                          onClick={() => selectItem(item, columnIndex)}
                          onDoubleClick={() => openItem(item, columnIndex)}
                          onKeyDown={(event) => handleItemKeyDown(event, item, columnIndex)}
                        >
                          {isDriveFolder(item)
                            ? <Folder size={17} />
                            : <DriveFileIcon item={item} size={18} />}
                          <span>{item.name}</span>
                          {isDriveFolder(item) && <ChevronRight size={14} />}
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}

              {selectedItem && !isDriveFolder(selectedItem) && (
                <aside className="drive-preview" aria-label="Dettagli file selezionato">
                  <span className="drive-preview-icon"><DriveFileIcon item={selectedItem} size={38} /></span>
                  <h2>{selectedItem.name}</h2>
                  <p>{driveFileKind(selectedItem)}</p>
                  <dl>
                    <div><dt>Posizione</dt><dd>{selectedItem.path}</dd></div>
                    <div><dt>Modificato</dt><dd>{formatModifiedTime(selectedItem.modifiedTime)}</dd></div>
                    {formatDriveFileSize(selectedItem.size) && <div><dt>Dimensione</dt><dd>{formatDriveFileSize(selectedItem.size)}</dd></div>}
                  </dl>
                  <button className="button button-primary" type="button" disabled={!selectedItem.webViewLink} onClick={openSelected}>
                    <ExternalLink size={16} /> Apri in Drive
                  </button>
                  <small>Il file si apre in sola visualizzazione secondo i permessi Drive.</small>
                </aside>
              )}
            </div>
            <footer className="drive-browser-footer">
              <span><ShieldCheck size={14} /> Non sono disponibili azioni di modifica o eliminazione.</span>
              <span>Doppio clic su un file per aprirlo.</span>
            </footer>
          </>
        )}
      </section>
    </main>
  );
}
